import QuillLocalStorage from 'core/utils/localStorageClient';
import { Typography, useTheme } from '@quillbot/design-system';
import { RootState } from 'src/_common/models/redux/store';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { firebase } from 'config/firebase';

const DismissableNoticeBar = dynamic(() => import('src/_common/components/systemNotifications/DismissableBar'));

interface BannerColors {
  bgColor: string;
  textColor: string;
}
interface SurveyBanner {
  surveyName: string;
  surveyMsg: string;
  surveyLink: string;
  btnLabel: string;
  showBanner: boolean;
  includePremium: boolean;
  excludeUnauthenticated: boolean;
  bannerExclusionPages?: Array<string>;
  bannerColors?: BannerColors;
}

export default function SurveyBanner({ setIsBannerShown }: { setIsBannerShown: Function }) {
  const isExtension = useSelector((state: RootState) => state.common.platform.isExtension);
  const isNative = useSelector((state: RootState) => state.common.platform.native);

  const isPremium = useSelector((rootState: RootState) => rootState?.user?.premium);
  const isPaused = useSelector((rootState: RootState) => rootState?.user?.isSubPaused) || false;
  const isAuthenticated = useSelector((rootState: RootState) => rootState?.user?.isAuthenticated);
  const [surveyBannerData, setSurveyBannerData] = useState<SurveyBanner>({
    surveyName: '',
    surveyMsg: '',
    surveyLink: '',
    btnLabel: '',
    showBanner: false,
    includePremium: true,
    excludeUnauthenticated: false,
    bannerExclusionPages: [],
    bannerColors: {
      bgColor: '',
      textColor: '',
    },
  });
  const router = useRouter();
  const pageUrl = router.pathname;
  const theme = useTheme();
  const [bgColor, setBgColor] = useState('');
  const [textColor, setTextColor] = useState('');

  const getBannerState = (data: SurveyBanner) => {
    const isSurveyDismissed = QuillLocalStorage.getInstance().get(data?.surveyName, 'false') === 'true';
    // If banner is already seen and dismissed, don't show banner
    if (isSurveyDismissed) return false;
    if (!data?.showBanner) return false;
    // Don't show banner on excluded pages
    if (data.bannerExclusionPages?.includes(pageUrl)) return false;
    // If campaign does not include premium and either user is premium
    // or subscription is paused, don't show banner
    if (!data?.includePremium && (isPremium || isPaused)) return false;
    // If campaign excludes unauthenticated users and
    // user is not authenticated, don't show banner
    if (data?.excludeUnauthenticated && !isAuthenticated) return false;
    return true;
  };

  useEffect(() => {
    async function init() {
      const { getDatabase, onValue, ref } = await firebase.database;

      if (!isExtension && !isNative) {
        onValue(ref(getDatabase(), 'surveyBannerConfig'), (snapshot: { val: Function }) => {
          const data = snapshot.val() as SurveyBanner;
          setSurveyBannerData({
            ...data,
            showBanner: getBannerState(data),
          });
          if (data.bannerColors?.bgColor) setBgColor(data.bannerColors.bgColor);
          if (data.bannerColors?.textColor) setTextColor(data.bannerColors.textColor);
          setIsBannerShown(getBannerState(data));
        });
      }
    }

    init();
  }, [isPaused, isPremium, isAuthenticated]);

  return (
    <>
      <DismissableNoticeBar
        onClick={() => {
          QuillLocalStorage.getInstance().set(surveyBannerData.surveyName, 'true');
          setSurveyBannerData({ ...surveyBannerData, showBanner: false });
          setIsBannerShown(false);
        }}
        isShown={surveyBannerData.showBanner}
        bgColor={bgColor}
        iconColor={textColor}>
        <Typography style={{ color: textColor || theme.palette.primary.contrastText }}>
          {surveyBannerData.surveyMsg}
          <u
            onClick={() => {
              QuillLocalStorage.getInstance().set(`${surveyBannerData.surveyName}`, 'true');
              setSurveyBannerData({ ...surveyBannerData, showBanner: false });
              window.open(surveyBannerData.surveyLink, '_blank');
            }}
            role="button"
            tabIndex={0}
            onKeyDown={() => {}}
            style={{
              cursor: 'pointer',
              marginLeft: '5px',
              marginTop: '5px',
            }}>
            {surveyBannerData.btnLabel}
          </u>
        </Typography>
      </DismissableNoticeBar>
    </>
  );
}
